import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Download, Calendar } from 'lucide-react';
import api from '../../utils/api';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import './Publications.css';

const API_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

const FILE_ICONS = { 'application/pdf': '📄', default: '📁' };

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function Publications() {
  const [pubs,   setPubs]   = useState([]);
  const [types,  setTypes]  = useState([]);
  const [total,  setTotal]  = useState(0);
  const [pages,  setPages]  = useState(1);
  const [page,   setPage]   = useState(1);
  const [type,   setType]   = useState('');
  const [year,   setYear]   = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedPub, setExpandedPub] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    if (type) params.set('type', type);
    if (year) params.set('year', year);
    api.get(`/publications?${params}`)
      .then(r => { setPubs(r.data.data); setTotal(r.data.total); setPages(r.data.pages); })
      .finally(() => setLoading(false));
  }, [page, type, year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/publication-types').then(r => setTypes(r.data)); }, []);

  const handleDownload = (pub) => {
    // Open PDF/file directly in new tab
    const fileUrl = `${API_BASE}${pub.file_path}`;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Header />
      <main>
        <div className="pub-hero">
          <div className="container">
            <h1 className="page-title" style={{color:'#fff'}}>Publikimet</h1>
            <p style={{color:'#b0c9e8',marginTop:6}}>Raporte, njoftime zyrtare, dokumente dhe plane strategjike</p>
          </div>
        </div>

        <div className="container pub-layout">
          {/* Filters */}
          <aside className="pub-sidebar">
            <div className="card pub-filter-card">
              <h3 className="filter-title">Filtro sipas llojit</h3>
              <button className={`filter-item ${!type ? 'active' : ''}`} onClick={() => { setType(''); setPage(1); }}>
                Të gjitha
              </button>
              {types.map(t => (
                <button key={t.id} className={`filter-item ${type === t.slug ? 'active' : ''}`}
                  onClick={() => { setType(t.slug); setPage(1); }}>
                  {t.name}
                </button>
              ))}
              <hr className="divider" style={{margin:'1rem 0'}} />
              <h3 className="filter-title">Viti</h3>
              <select className="form-input form-select" value={year} onChange={e => { setYear(e.target.value); setPage(1); }}>
                <option value="">Të gjitha vitet</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </aside>

          {/* Publications list */}
          <div className="pub-main">
            <p className="result-count">{total} publikime gjithsej</p>
            {loading ? <div className="spinner" /> : (
              <>
                <div className="pub-grid">
                  {pubs.map(pub => (
                    <div key={pub.id} className="pub-card card">
                      <div className="pub-icon-wrap">
                        <span className="pub-file-icon">
                          {FILE_ICONS[pub.file_type] || FILE_ICONS.default}
                        </span>
                        {pub.type_name && <span className="badge pub-type-badge">{pub.type_name}</span>}
                      </div>
                      <div className="pub-info">
                        <h3 className="pub-title">{pub.title}</h3>
                        {/* Përshkrimi shfaqet vetëm kur klikohet "Shiko" — jo inline */}
                        <div className="pub-meta">
                          {pub.year && (
                            <span className="pub-meta-item"><Calendar size={12}/> {pub.year}</span>
                          )}
                          {pub.file_size && (
                            <span className="pub-meta-item"><FileText size={12}/> {formatSize(pub.file_size)}</span>
                          )}
                          {pub.file_path && !pub.file_path.includes('placeholder') && (
                            <span className="pub-meta-item"><Download size={12}/> {pub.downloads} shkarkime</span>
                          )}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {/* Butoni Shkarko — vetëm nëse ka skedar real */}
                        {pub.file_path && !pub.file_path.includes('placeholder') && (
                          <button
                            className="btn btn-primary btn-sm pub-dl-btn"
                            onClick={() => handleDownload(pub)}
                            title="Hap dokumentin në tab të ri"
                          >
                            <Download size={14}/> Shkarko
                          </button>
                        )}
                        {/* Butoni Shiko — nëse ka përshkrim HTML */}
                        {pub.description && (
                          <button
                            className="btn btn-secondary btn-sm pub-dl-btn"
                            onClick={() => setExpandedPub(expandedPub === pub.id ? null : pub.id)}
                            title="Shfaq përshkrimin"
                          >
                            {expandedPub === pub.id ? '▲ Mbyll' : '▼ Shiko'}
                          </button>
                        )}
                      </div>
                      {/* Përshkrimi HTML — renderohet korrekt */}
                      {expandedPub === pub.id && pub.description && (
                        <div
                          className="pub-expanded-desc"
                          dangerouslySetInnerHTML={{ __html: pub.description }}
                          style={{
                            gridColumn:'1/-1',
                            marginTop:10,
                            padding:'14px 18px',
                            background:'var(--gray-50)',
                            borderRadius:8,
                            fontSize:'0.875rem',
                            color:'var(--gray-700)',
                            lineHeight:1.75,
                            borderLeft:'3px solid var(--blue-300)',
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {pubs.length === 0 && <p className="empty-state">Nuk ka publikime.</p>}
                {pages > 1 && (
                  <div className="pagination">
                    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                      <button key={p} className={`page-btn ${p === page ? 'active' : ''}`}
                        onClick={() => setPage(p)}>{p}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
